import { getAuthRequestHeaders } from "@qw/auth";
import PostalMime, { type Address, type Attachment, type Email } from "postal-mime";
import { type CSSProperties, useEffect, useState } from "react";
import { getFileDownloadUrl, getFilePreviewKind, type PartialFileUpload } from "../utils";
import { Loading } from "./loading";

export const FilePreviewFrame = ({
  file,
  headers,
  style,
}: {
  file: PartialFileUpload;
  headers?: HeadersInit;
  style?: CSSProperties;
}) => {
  const [url, setURL] = useState("");
  const [email, setEmail] = useState<Email | undefined>();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setURL("");
    setEmail(undefined);
    setError(null);

    const previewKind = getFilePreviewKind(file.mimeType);
    const resolvedHeaders = headers ?? getAuthRequestHeaders();
    const postal = new PostalMime();
    let blobUrl: string | null = null;
    let isActive = true;

    const loadFile = async () => {
      if (!previewKind) {
        throw new Error(`Unsupported preview type: ${file.mimeType}`);
      }

      // A blob URL has no filename, so Chrome's PDF toolbar would save and print as a UUID.
      // Load PDFs from an inline signed URL that carries the real filename instead.
      if (previewKind === "pdf") {
        const { disposition, signedUrl } = await getFileDownloadUrl(file.id, {
          disposition: "inline",
          headers: resolvedHeaders,
        });

        // An API without inline support signs an attachment URL, which the iframe would download on load.
        if (signedUrl && disposition === "inline") {
          if (isActive) {
            setURL(signedUrl);
          }
          return;
        }
      }

      const response = await fetch(`/api/files/${file.id}`, { headers: resolvedHeaders });

      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const processedBlob = blob.slice(0, blob.size, file.mimeType);

      if (!isActive) {
        return;
      }

      if (previewKind === "email") {
        const email = await postal.parse(processedBlob);

        if (isActive) {
          setEmail(email);
        }
        return;
      }

      blobUrl = URL.createObjectURL(processedBlob);
      setURL(blobUrl);
    };

    void loadFile().catch((error_) => {
      if (isActive) {
        setError(error_ instanceof Error ? error_.message : "Failed to load file");
      }
    });

    return () => {
      isActive = false;

      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [file, headers]);

  if (error) {
    return (
      <>
        <p className="text-destructive mb-2">Error loading file</p>
        <p className="text-sm text-muted-foreground">{error}</p>
      </>
    );
  }

  if (email) {
    return <EmailDetails email={email} />;
  }

  if (!url) {
    return <Loading label="Loading..." />;
  }

  return (
    <iframe
      src={`${url}#zoom=100`}
      style={{
        width: "100%",
        height: "100dvh",
        display: "block",
        ...style,
      }}
    />
  );
};

const EmailDetails = ({ email }: { email: Email }) => {
  const bodyText = getEmailBodyText(email);

  const renderAddresses = (addresses: Address[] | undefined) =>
    addresses?.map((address, index) => (
      <span key={index}>
        {address.name ? `${address.name} <${address.address}>` : address.address}
        {index < addresses.length - 1 ? ", " : ""}
      </span>
    ));

  const renderAttachments = (attachments: Attachment[]) =>
    attachments.map((attachment, index) => <li key={index}>{attachment.filename}</li>);

  return (
    <div
      style={{
        width: "80vw",
        height: "calc(100% - 4vh)",
        display: "block",
        margin: "2vh auto",
        overflow: "auto",
        padding: "20px",
        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
        borderRadius: "8px",
        backgroundColor: "#fff",
      }}
    >
      <h2>{email.subject || "No Subject"}</h2>
      <div>
        <strong>From:</strong> {email.from ? renderAddresses([email.from]) : null}
      </div>
      <div>
        <strong>To:</strong> {renderAddresses(email.to)}
      </div>
      {email.cc && (
        <div>
          <strong>CC:</strong> {renderAddresses(email.cc)}
        </div>
      )}
      {email.date && (
        <div>
          <strong>Date:</strong> {email.date}
        </div>
      )}
      <hr />
      <div style={{ margin: "20px 0", overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}>{bodyText || "No body"}</div>
      {email.attachments.length > 0 && (
        <div>
          <strong>Attachments:</strong>
          <ul>{renderAttachments(email.attachments)}</ul>
        </div>
      )}
    </div>
  );
};

const HTML_BLOCK_PATTERN =
  /<\/?(?:address|article|aside|blockquote|br|dd|div|dl|dt|figcaption|figure|footer|h[1-6]|header|hr|li|main|nav|ol|p|pre|section|table|tbody|td|tfoot|th|thead|tr|ul)\b[^>]*>/gi;
const HTML_DANGEROUS_CONTENT_PATTERN =
  /<\s*(script|style|noscript|iframe|object|embed)\b[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi;
const HTML_TAG_PATTERN = /<[^>]*>/g;
const HTML_ENTITY_PATTERN = /&(?:#(\d+)|#x([\da-f]+)|([a-z]+));/gi;
const NAMED_HTML_ENTITIES: Record<string, string> = {
  amp: "&",
  apos: "'",
  gt: ">",
  lt: "<",
  nbsp: " ",
  quot: '"',
};

const getEmailBodyText = (email: Email) => {
  const text = email.text?.trim();

  if (text) {
    return text;
  }

  return email.html ? htmlToPlainText(email.html) : "";
};

const htmlToPlainText = (html: string) =>
  decodeHtmlEntities(
    html
      .replace(HTML_DANGEROUS_CONTENT_PATTERN, "")
      .replace(HTML_BLOCK_PATTERN, "\n")
      .replace(HTML_TAG_PATTERN, "")
      .replace(/[ \t\f\v]+/g, " ")
      .replace(/\n[ \t\f\v]+/g, "\n")
      .replace(/[ \t\f\v]+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim()
  );

const decodeHtmlEntities = (text: string) =>
  text.replace(
    HTML_ENTITY_PATTERN,
    (match, decimalEntity: string | undefined, hexEntity: string | undefined, namedEntity: string | undefined) => {
      if (decimalEntity || hexEntity) {
        const codePoint = Number.parseInt(decimalEntity ?? hexEntity ?? "", decimalEntity ? 10 : 16);

        return Number.isSafeInteger(codePoint) && codePoint >= 0 && codePoint <= 0x10ffff
          ? String.fromCodePoint(codePoint)
          : match;
      }

      return namedEntity ? (NAMED_HTML_ENTITIES[namedEntity.toLowerCase()] ?? match) : match;
    }
  );
